// The destination CATALOG + phrase GROUNDING for the navigation capability. Otto's brain (the backend)
// only captures WHICH page the user named as a phrase; the real set of destinations — and whether the
// user may reach them — lives on the frontend, exactly like the filter contract. So this module builds
// the permission-scoped list of navigable surfaces (from the SAME checkPermission the side menu uses) and
// grounds the model's phrase against it. A page the user can't see isn't in the list, so Otto can't send
// them there. When the platform's pages/permissions change, this list follows — nothing here is invented.
//
// FIRST SLICE ("named surfaces first"): the top-bar Recommendations action + the global side-menu sections.
// The eventual wide-fix is to derive this from a shared builder that the side menu (SideMenu.jsx menuData)
// also consumes — the same extraction we did for the filter fields (filterFieldBuilders.js). Until then the
// URLs/permission keys below mirror menuData verbatim.
import { checkPermission } from "../../../../config/utils";
import { permissions } from "../../../../config/permissions";
import { normalizeText } from "../../core/normalizeText";
import { rankPageTargets, pickPageTarget } from "../planner/pageRanking";

// Re-exported from its own dependency-free module (core/normalizeText.js) so importing a string normalizer
// no longer drags this file's permission imports along. Every existing call site keeps working unchanged.
export { normalizeText };

// THE DASHBOARD GATE (mirrors DashboardSwitcher.jsx, a non-import mirror registered with otto-sync).
// Upstream FCA-7421 took both dashboards OUT of SideMenu.menuData and put them in a DashboardSwitcher card,
// which gates them on TWO permissions, not one: the shared `menu.dashboard` entry plus that dashboard's own
// `charts_and_graph.<chartPermission>.view`. Otto used to gate the Asset Dashboard on `menu.asset_dashboard`
// -- a key the switcher no longer consults at all, leaving Otto the last reader in src/. Mirrored here as
// literals rather than imported because DashboardSwitcher.jsx is a React component that pulls the redux
// store in at module scope, and eager-importing that graph into this catalog is exactly what broke the nav
// tests once before (see navExecutor's lazy-require comment). `xrepo-dashboard-switcher` guards the mirror.
const DASHBOARD_MENU_PERMISSION = "dashboard";

// key -> the chart permission that dashboard's own switcher entry is gated on. Keys match DASHBOARD_OPTIONS.
const DASHBOARD_CHART_PERMISSION = {
    dashboard: "dashboard",
    assetDashboard: "assets"
};

// Fail-closed exactly like the switcher: no menu entry, or no chart permission, means no destination. The
// switcher builds its list the same way (canSeeDashboardMenu() && checkPermission("charts_and_graph", ...)),
// so a user who cannot see the card cannot be sent there by Otto either.
const canOpenDashboard = key =>
    checkPermission("menu", DASHBOARD_MENU_PERMISSION, null) && checkPermission("charts_and_graph", DASHBOARD_CHART_PERMISSION[key], "view");

const readLocalStorage = key => {
    try {
        return typeof window !== "undefined" && window.localStorage ? window.localStorage.getItem(key) : null;
    } catch (e) {
        return null;
    }
};

// GLOBAL side-menu sections: single-URL, permission-gated destinations (mirrors SideMenu.menuData). Each
// entry's `view` is a checkPermission() thunk (evaluated at build time so we never OFFER a page the user
// can't open) and `synonyms` are the normalized ways a user might name it. `url` is a full router path.
const globalTargets = () => [
    {
        key: "landing-page",
        label: "Landing Page",
        url: "/home",
        synonyms: ["landing page", "landing", "home page", "home"],
        view: () => readLocalStorage("hasLandingPage") === "true"
    },
    {
        key: "dashboard",
        label: "FCA Dashboard",
        url: "/dashboard",
        synonyms: ["dashboard", "fca dashboard", "the dashboard", "main dashboard"],
        view: () => canOpenDashboard("dashboard")
    },
    {
        // The platform's SECOND dashboard (asset-scoped, added upstream in FCA-6997). Deliberately NOT given
        // an "assets" synonym: that word is already an exact synonym of `assetmanagement`, and adding it here
        // would turn "go to assets" into a tie that asks instead of navigating. "asset dashboard" is exact
        // here and only a 0.8 contains-match on `dashboard`, so each dashboard still grounds unambiguously.
        key: "assetDashboard",
        label: "Asset Dashboard",
        url: "/assetdashboard",
        synonyms: ["asset dashboard", "assets dashboard", "asset dashboard page"],
        view: () => canOpenDashboard("assetDashboard")
    },
    {
        key: "projects",
        label: "FCA Projects",
        url: "/project",
        synonyms: ["projects", "fca projects", "project list", "the projects", "projects page"],
        view: () => checkPermission("menu", "fca_projects", null)
    },
    {
        key: "smartCharts",
        label: "Smart Reports & Charts",
        url: "/smartcharts/reports",
        // "reports" resolves here (the reports area). The dashboard's top-bar Reports button opens an
        // in-page modal rather than navigating, so a route is the grounded destination for "go to reports".
        synonyms: ["smart reports and charts", "smart reports & charts", "smart reports", "smart charts", "reports and charts", "reports", "report", "reports page"],
        view: () => checkPermission("menu", permissions.SMART_CHARTS, null)
    },
    {
        key: "efci",
        label: "EFCI Benchmarking",
        url: "/efci",
        synonyms: ["efci", "efci benchmarking", "benchmarking", "efci page"],
        view: () => checkPermission("menu", "efci_benchmarking", null)
    },
    {
        key: "energyManagement",
        label: "Energy Management",
        url: "/energyManagement",
        synonyms: ["energy management", "energy mgmt", "energy", "energy page"],
        view: () => checkPermission("menu", "energy_management", null)
    },
    {
        key: "assetmanagement",
        label: "Asset Management",
        url: "/assetmanagement",
        synonyms: ["asset management", "asset mgmt", "assets", "asset management page", "assets area"],
        view: () => checkPermission("menu", "asset_management", null)
    },
    {
        key: "images",
        label: "Image Management",
        url: "/images",
        synonyms: ["image management", "image mgmt", "images", "photos", "image management page"],
        view: () => checkPermission("menu", "image_management", null)
    },
    {
        key: "initiatives",
        label: "Project Initiatives",
        url: "/Initiatives",
        synonyms: ["project initiatives", "initiatives", "initiatives page"],
        view: () => checkPermission("menu", "initiatives", null)
    },
    {
        key: "documents",
        label: "Document Management",
        url: "/documents",
        synonyms: ["document management", "document mgmt", "documents", "docs", "document management page"],
        view: () => checkPermission("menu", "documents", null)
    },
    {
        // LABEL: the table's own name, not the side menu's. The menu item says "Mobile Solutions" but the
        // page it opens is the Asset Survey Projects grid, and this label is what Otto calls the destination
        // back to the user — "Asset Survey Projects Table View" (it is a registered table_view page since
        // FTO-107), where "Mobile Solutions Table View" names nothing anyone can point at. The menu wording
        // stays first in the synonyms, so "go to mobile solutions" still grounds here.
        key: "mobile-solutions",
        label: "Asset Survey Projects",
        url: "/asset-survey-project",
        synonyms: ["mobile solutions", "mobile", "asset survey projects", "survey projects", "mobile solution"],
        view: () => checkPermission("menu", "mobile_solution", "asset_survey_project")
    },
    {
        // The first SETTINGS CHILD in this catalog (added upstream in FCA-6998). The other Settings children
        // stay out by design — see the header note — but this one is a top-level route (`/dynamic-tables`, not
        // a section of the settings page) and menuData marks it isQuickLink, so users name it the way they name
        // the global sections. "tables" USED to need no synonym here — it scored 0.8 by containment and nothing
        // else in the catalog carried the word — but FCA-7057 gave Client Dynamic Tables its own route below,
        // and that label contains "tables" too, so the bare noun became a 0.8-vs-0.8 tie that asked instead of
        // navigating. Listing it explicitly restores the documented behaviour the honest way: an exact synonym
        // (1.0) outranks the other label's containment (0.8). This is only ever a tie-break for someone holding
        // BOTH permissions — the catalog is permission-scoped, so a client user who can't open this settings
        // page never sees it as a candidate and their "tables" still grounds to Client Dynamic Tables.
        key: "dynamicTables",
        label: "Dynamic Tables",
        url: "/dynamic-tables",
        synonyms: ["dynamic tables", "dynamic table", "dynamic tables page", "dynamic tables settings", "tables"],
        view: () => checkPermission("menu", "settings", permissions.DYNAMIC_TABLES)
    },
    {
        // Mirrorable only since FCA-7057: this used to be a menu PARENT whose children were the client's own
        // published tables, so the destinations were runtime rows and their names (client-authored) would have
        // entered Otto's nav vocabulary and collided with filter fields/values. Upstream replaced all of it
        // with ONE static top-level route + its own permission, which is exactly the shape this catalog can
        // mirror — so only the parent is declared here and no client-authored name is ever grounded.
        // No bare "tables"/"dynamic tables" synonym: those are exact on `dynamicTables` above and would turn
        // an unambiguous phrase into a tie. "client" is the discriminator, so every synonym here carries it.
        // NOTE: SideMenu additionally hides this entry when the client has no published tables
        // (`publishedDynamicTables.length > 0`) — redux state a permission thunk can't read, so Otto mirrors
        // the permission half only and may offer the page to a permitted user who'd land on its empty state.
        key: "clientDynamicTables",
        label: "Client Dynamic Tables",
        url: "/client-dynamic-tables",
        synonyms: ["client dynamic tables", "client dynamic table", "client tables", "client dynamic tables page"],
        view: () => checkPermission("menu", permissions.CLIENT_DYNAMIC_TABLES, null)
    }
];

// CONTEXT actions: PROJECT-LEVEL destinations that depend on the current dashboard context (a selected
// single project). These mirror the `sidePanelCollapseOne` / top-bar buttons EXACTLY — URL + navigation
// state + the 3-level breadcrumb (Dashboard -> <project name> -> <page>) — so navigating here is
// indistinguishable from clicking the on-screen button (same trail, and the destination page opens
// pre-scoped to THIS project with its filters already active). This is what sets them apart from the
// GLOBAL pages below, which carry no project pre-filter. `projectName` is threaded from the container
// (same redux/cache source the buttons use). Only available when there IS a single project in scope; the
// container omits them otherwise. The handler sources are SideMenu.jsx handleRegionView/handleSiteView/
// handleBuildingView/handleAdditionsView/handleFloorsView/handleRecommentationView + topFilter
// handleCapitalRequestView.
const contextTargets = ({ singleProjectId, projectName, capitalRequestLabel } = {}) => {
    if (singleProjectId == null || singleProjectId === "") return [];
    const base = `/project/projectinfo/${singleProjectId}`;
    // A project-level page + its 3-level breadcrumb, matching the on-screen button verbatim.
    const page = (key, label, slug, { state, synonyms } = {}) => {
        const url = `${base}/${slug}`;
        return {
            key,
            label,
            url,
            ...(state ? { state } : {}),
            bc: [
                { key: "dashboard", name: "Dashboard", path: "/dashboard", index: 0 },
                { key: "projectName", name: projectName || "", path: `${base}/basicdetails`, index: 1 },
                { key: "info", name: label, path: url, index: 2 }
            ],
            synonyms: synonyms || [],
            view: () => true
        };
    };
    const crLabel = capitalRequestLabel || "Capital Request";
    // Recommendations stays FIRST (the original context action; its shape/order is asserted by tests).
    return [
        page("recommendations", "Recommendations", "recommendations", {
            state: { from: "dashboard" },
            synonyms: ["recommendations", "recommendation", "recs", "recommendation list", "recommendations page",
                "project recommendations", "the project recommendations"]
        }),
        page("cr-recommendations", crLabel, "cr-recommendations", {
            state: { from: "dashboard" },
            synonyms: [normalizeText(crLabel), "capital request", "capital requests", "capital request page",
                "capital planning", "project capital request"].filter(Boolean)
        }),
        page("regions", "Regions", "regions", {
            synonyms: ["regions", "region", "regions page", "project regions", "the project regions"]
        }),
        page("sites", "Sites", "sites", {
            synonyms: ["sites", "site", "sites page", "project sites", "the project sites"]
        }),
        page("buildings", "Buildings", "buildings", {
            synonyms: ["buildings", "building", "buildings page", "project buildings", "the project buildings"]
        }),
        // Additions live at the buildingAddition route (label stays "Additions").
        page("additions", "Additions", "buildingAddition", {
            synonyms: ["additions", "addition", "building additions", "additions page", "project additions"]
        }),
        page("floors", "Floors", "floors", {
            synonyms: ["floors", "floor", "floors page", "project floors", "the project floors"]
        }),
        // The project's own page (overview / basic details). Destination IS basicdetails, so the breadcrumb
        // is the 2-level Dashboard -> <project name> trail (no duplicate third crumb).
        {
            key: "project-overview",
            label: "Project Overview",
            url: `${base}/basicdetails`,
            bc: [
                { key: "dashboard", name: "Dashboard", path: "/dashboard", index: 0 },
                { key: "projectName", name: projectName || "", path: `${base}/basicdetails`, index: 1 }
            ],
            synonyms: ["project page", "the project page", "project overview", "project details", "the project",
                "project home", "project info", "basic details", "project basic details"],
            view: () => true
        }
    ];
};

// CLIENT-scoped asset-hub destinations — the asset-family twin of contextTargets above. The Asset Dashboard
// drills into one client's asset hub, whose tabs live under /assetmanagement/assetinfo/<clientId>/. Without a
// client there IS no hub, so this returns [] and "take me to the assets table" falls back to the module
// landing — the same fail-closed rule the whole catalog runs on: never offer a page the user cannot reach.
//
// URLs, tab keys and permissions MIRROR assetManagement/index.jsx's `infoTabsData` verbatim (a non-import
// mirror, registered with otto-sync). The `?parentSection=asset` query is part of the destination, not
// decoration: the container reads it to render the asset-hub chrome.
const assetHubTargets = ({ clientId, clientName } = {}) => {
    // Two prerequisites, both fail-closed. The CLIENT (no client -> no hub, see above) and the MODULE itself:
    // every one of these tabs lives inside Asset Management, so a user without that menu permission cannot
    // open any of them no matter what their per-form permissions say (the global `assetmanagement` target
    // above is gated the same way). The per-tab `view` thunks below gate only the FORM, so without this the
    // hub would be offered to an Asset-Dashboard-only user who has `forms.assets.view` — a page they'd bounce off.
    if (clientId == null || clientId === "" || !checkPermission("menu", "asset_management", null)) return [];
    const base = `/assetmanagement/assetinfo/${clientId}`;
    const q = "?parentSection=asset";
    // Named `page`, matching contextTargets' own helper (the otto-sync xrepo-page-nav-keys gate parses
    // `page("<key>", ...)` calls as catalog target keys — the same helper name keeps this mirror discoverable).
    const page = (key, label, slug, { state, synonyms, view } = {}) => {
        const url = `${base}/${slug}${q}`;
        return {
            key,
            label,
            url,
            ...(state ? { state } : {}),
            bc: [
                { key: "assetDashboard", name: "Asset Dashboard", path: "/assetdashboard", index: 0 },
                { key: "clientName", name: clientName || "", path: `${base}/basicdetails${q}`, index: 1 },
                { key: "info", name: label, path: url, index: 2 }
            ],
            synonyms: synonyms || [],
            view: view || (() => true)
        };
    };
    return [
        // Assets FIRST — it is the hub's principal grid and the destination this family exists for.
        page("assets", "Assets", "assets", {
            state: { isInitialView: true },
            synonyms: ["assets", "asset", "assets table", "asset table", "assets list", "asset list",
                "assets grid", "asset register", "assets tab"],
            view: () => checkPermission("forms", "assets", "view")
        }),
        page("assetRegions", "Regions", "regions", {
            synonyms: ["regions", "region", "asset regions", "the asset regions"],
            view: () => checkPermission("forms", "regions", "view")
        }),
        page("assetSites", "Sites", "sites", {
            synonyms: ["sites", "site", "asset sites", "the asset sites"],
            view: () => checkPermission("forms", "sites", "view")
        }),
        page("assetBuildings", "Buildings", "buildings", {
            synonyms: ["buildings", "building", "asset buildings", "the asset buildings"],
            view: () => checkPermission("forms", "buildings", "view")
        }),
        page("mobileAssets", "Mobile Assets", "survey-assets", {
            synonyms: ["mobile assets", "survey assets", "surveyed assets", "mobile asset list"],
            view: () => checkPermission("forms", permissions.MOBILE_ASSETS, "view")
        }),
        page("assetClient", "Client", "basicdetails", {
            synonyms: ["client", "the client", "client details", "client record", "client page"],
            view: () => checkPermission("forms", "clients", "view")
        })
    ];
};

// Build the permission-scoped destination catalog for THIS user/context. Only viewable targets are
// returned, so grounding can never resolve to a page the user can't reach.
export const buildNavigationTargets = ({ singleProjectId, projectIds, projectName, capitalRequestLabel, clientId, clientName } = {}) => {
    const spid = singleProjectId != null ? singleProjectId : projectIds && projectIds.length === 1 ? projectIds[0] : undefined;
    const all = [
        ...contextTargets({ singleProjectId: spid, projectName, capitalRequestLabel }),
        ...assetHubTargets({ clientId, clientName }),
        ...globalTargets()
    ];
    return all
        .filter(t => {
            try {
                return t.view ? !!t.view() : true;
            } catch (e) {
                return false; // a permission check that throws -> treat as not visible (fail closed)
            }
        })
        .map(({ view, ...rest }) => rest); // drop the thunk; callers only need the data
};

// Score one target against a normalized phrase. 1.0 = exact label/synonym; 0.8 = one contains the other;
// otherwise a token-overlap fraction (how much of the target's best label the phrase covers). 0 = no overlap.
const scoreTarget = (phrase, target) => {
    const labels = [normalizeText(target.label), ...(target.synonyms || []).map(normalizeText)].filter(Boolean);
    const phraseTokens = new Set(phrase.split(" ").filter(Boolean));
    let best = 0;
    labels.forEach(label => {
        if (!label) return;
        if (label === phrase) {
            best = Math.max(best, 1);
            return;
        }
        if (phrase.includes(label) || label.includes(phrase)) {
            best = Math.max(best, 0.8);
            return;
        }
        const labelTokens = label.split(" ").filter(Boolean);
        if (!labelTokens.length) return;
        const shared = labelTokens.filter(t => phraseTokens.has(t)).length;
        if (shared) best = Math.max(best, (shared / labelTokens.length) * 0.7);
    });
    return best;
};

// GROUND a destination phrase against the catalog. Returns:
//   { decision: "auto",    target, candidates }  — one confident destination (frontend still CONFIRMS)
//   { decision: "clarify", candidates }          — several plausible destinations, ask which
//   { decision: "none",    candidates }          — nothing matched a reachable page
// Confirm-before-navigate is applied by the proposal layer, so even an "auto" match shows a confirm card.
//
// `ctx.page` (the page the user is ON) turns a TIE into an answer. Page names are not unique here — the
// project hub and the asset hub both have Regions/Sites/Buildings, and there are three dashboards — so
// without it "buildings" is a permanent coin-flip that has to be asked about. With it, a tie resolves to
// the candidate in the current page's FAMILY: on the Asset Dashboard "buildings" means asset buildings, on
// the FCA Dashboard it means the project's, and neither asks. Ties that family can't split still clarify
// (grounding never guesses), but the offered list is ranked and capped rather than enumerated.
// Omitting ctx keeps the pre-existing behavior exactly, so callers that have no page context lose nothing.
const resolveTie = (candidates, ctx) => {
    const opts = { page: ctx && ctx.page, utterance: ctx && ctx.utterance };
    const winner = pickPageTarget(candidates, opts);
    if (winner) return { decision: "auto", target: winner, candidates };
    return { decision: "clarify", candidates: rankPageTargets(candidates, opts) };
};

export const groundDestination = (phrase, targets, ctx) => {
    const p = normalizeText(phrase);
    if (!p || !targets || !targets.length) return { decision: "none", candidates: [] };
    const scored = targets
        .map(target => ({ target, score: scoreTarget(p, target) }))
        .filter(s => s.score > 0)
        .sort((a, b) => b.score - a.score);
    if (!scored.length) return { decision: "none", candidates: [] };

    // An exact label/synonym match wins outright (a unique one auto-confirms; a tie is split by family).
    const exact = scored.filter(s => s.score >= 0.999);
    if (exact.length === 1) return { decision: "auto", target: exact[0].target, candidates: scored.map(s => s.target) };
    if (exact.length > 1) return resolveTie(exact.map(s => s.target), ctx);

    // Strong (contains) matches: one -> confirm it; several -> family, then ask.
    const strong = scored.filter(s => s.score >= 0.8);
    if (strong.length === 1) return { decision: "auto", target: strong[0].target, candidates: scored.map(s => s.target) };
    if (strong.length > 1) return resolveTie(strong.map(s => s.target), ctx);

    // Only weaker token matches remain: a single clear one confirms; otherwise offer the top few.
    const near = scored.filter(s => s.score >= 0.5);
    if (near.length === 1) return { decision: "auto", target: near[0].target, candidates: scored.map(s => s.target) };
    if (near.length > 1) return resolveTie(near.map(s => s.target), ctx);
    return resolveTie(scored.slice(0, 4).map(s => s.target), ctx);
};
